import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { UserService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'

export interface UseAdminWaitingListParams {
  sortField?: keyof UserService.WaitingListEntry
  sortDirection?: 'asc' | 'desc'
  initialPageSize?: number
}

const DEFAULT_PAGE_SIZE = 30

export const adminWaitingListQueryKeys = {
  all: () => ['admin-waiting-list'] as const,
  list: (params: { sortField?: keyof UserService.WaitingListEntry; sortDirection?: 'asc' | 'desc' }) =>
    ['admin-waiting-list', params] as const,
}

const fetchAdminWaitingListPage = async ({
  pageParam,
  sortField,
  sortDirection,
}: {
  pageParam: { limit: number; offset: number }
  sortField?: keyof UserService.WaitingListEntry
  sortDirection?: 'asc' | 'desc'
}): Promise<UserService.WaitingListEntry[]> => {
  const { limit, offset } = pageParam
  const response = await apis.userService.waitingListGet(
    sortField as UserService.WaitingListSortFieldEnum | undefined,
    sortDirection as UserService.SortDirectionEnum | undefined,
    limit,
    offset
  )
  return response.data.subscriptions
}

export const useAdminWaitingList = ({
  sortField,
  sortDirection,
  initialPageSize = DEFAULT_PAGE_SIZE,
}: UseAdminWaitingListParams) => {
  const query = useInfiniteQuery({
    queryKey: adminWaitingListQueryKeys.list({ sortField, sortDirection }),
    queryFn: ({ pageParam }) =>
      fetchAdminWaitingListPage({
        pageParam: pageParam ?? { limit: initialPageSize, offset: 0 },
        sortField,
        sortDirection,
      }),
    initialPageParam: { limit: initialPageSize, offset: 0 },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length === 0) return undefined
      const currentOffset = allPages.reduce((sum, page) => sum + page.length, 0)
      const nextLimit = allPages.length === 1
        ? initialPageSize * 2
        : Math.min(initialPageSize * 2 ** allPages.length, initialPageSize * 2 ** 8)
      return { limit: nextLimit, offset: currentOffset }
    },
  })

  const waitingList = useMemo(() => query.data?.pages.flat() ?? [], [query.data])
  const hasMore = !!query.hasNextPage

  return {
    waitingList,
    hasMore,
    isInitialLoading: query.isPending,
    isFetchingNextPage: query.isFetchingNextPage,
    isError: query.isError,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  }
}
